import React, {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
} from "react";

import { Button } from "../Button/Button";
import type { IconType } from "../Icon/Icon";
import { svgImportKeys } from "../Icon/Icon";

interface TabElementProps {
  icon?: IconType;
  notice?: "attention" | "required";
  tab: string;
}

interface TabsBarProps {
  activeIndex: number;
  children: ReactNode;
  onTabChange: (index: number) => void;
}

const isTabElement = (
  child: ReactElement,
): child is ReactElement<TabElementProps> => {
  if (!child.props || typeof child.props !== "object") {
    return false;
  }
  const props = child.props as Record<string, unknown>;
  return (
    "tab" in props &&
    typeof props.tab === "string" &&
    (!props.icon ||
      (typeof props.icon === "string" &&
        svgImportKeys.includes(props.icon as IconType))) &&
    (!props.notice ||
      props.notice === "attention" ||
      props.notice === "required")
  );
};

const getNoticeClass = (notice?: "attention" | "required") => {
  if (notice === "attention") {
    return "relative before:content-[''] before:absolute before:rounded before:bg-red-500 before:w-[7px] before:h-[7px] before:top-[calc(50%-8px)] before:right-[7px]";
  }
  if (notice === "required") {
    return "relative before:content-['*'] before:absolute before:top-[calc(50%-12px)] before:right-[6px]";
  }
  return "";
};

const selectedTabClass =
  "text-accent-tab-primary hover:text-accent-tab-primary hover:bg-accent-tab-faint";

export const TabsBar = ({
  activeIndex = 0,
  children,
  onTabChange,
}: TabsBarProps) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const activeTab = tabRefs.current[activeIndex];

    const setIndicator = () => {
      if (indicatorRef.current && tabRefs.current[activeIndex]) {
        if (activeTab) {
          indicatorRef.current.style.width = `${activeTab.offsetWidth}px`;
          indicatorRef.current.style.transform = `translateX(${activeTab.offsetLeft}px)`;
          activeTab.classList.remove(
            "text-primary-950",
            "hover:bg-primary-50",
            "hover:text-primary-700",
            "active:bg-primary-100",
          );
        }
      }
    };

    setIndicator();

    const resizeObserver = new ResizeObserver(setIndicator);
    if (activeTab) {
      resizeObserver.observe(activeTab);
    }

    return () => {
      if (activeTab) {
        resizeObserver.unobserve(activeTab);
      }
    };
  }, [activeIndex, children]);

  return (
    <div className="border-primary-300 relative border-b">
      <div className="flex" role="tablist">
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child) || !isTabElement(child)) {
            return null;
          }

          const { icon, notice, tab } = child.props;

          return (
            <span>
              <Button
                className={[
                  getNoticeClass(notice),
                  activeIndex == index ? selectedTabClass : "",
                ].join(" ")}
                data-testid={`tab-${tab}${
                  notice === "attention" ? "-notice" : ""
                }`}
                icon={icon}
                label={tab}
                onClick={() => {
                  history.pushState({ some: "state" }, "", `#${tab}`);
                  onTabChange(index);
                }}
                ref={(el: HTMLButtonElement | null) => {
                  if (el) {
                    tabRefs.current[index] = el;
                  } else {
                    tabRefs.current[index] = null;
                  }
                }}
              />
            </span>
          );
        })}
      </div>
      <div
        className={`
          bg-accent-tab-primary absolute bottom-0 h-0.5 transition-transform
          duration-200
          dark:bg-primary-50
        `}
        ref={indicatorRef}
        style={{ width: 0 }}
      />
    </div>
  );
};
